// The store: every read the screens make, and the only writer.
//
// The personal solver runs client side on local data, so the grid re-solves as a
// rating changes and planning works with no network at all. The group layer is
// computed here too while the application is local-first; sync.ts is the seam that
// moves it server side, where private ratings can be combined without being
// distributed to other clients.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_CONFIG } from '../engine/config.ts';
import type { TuningConfig } from '../engine/config.ts';
import { detectConflicts } from '../engine/conflicts.ts';
import { buildMemberViews } from '../engine/normalize.ts';
import { ledgerWeights } from '../engine/group.ts';
import { planFromSolve, solveDay } from '../engine/solver.ts';
import type { RestRequirement, SolveResult } from '../engine/solver.ts';
import { buildUtilityMap, seedRatings } from '../engine/utility.ts';
import { buildDraft, draftPicks } from '../engine/draft.ts';
import { pickedLineupChanges } from '../engine/lineup-changes.ts';
import type { LineupChange } from '../engine/lineup-changes.ts';

export const LINEUP_CHANGES_KEY = 'lineupChanges';
import { EMOTIONS } from '../engine/preferences.ts';
import type { SeenBefore } from '../engine/preferences.ts';
import { findClashes, resolvePicks } from '../engine/picks.ts';
import type { Clash, ResolvedPick } from '../engine/picks.ts';
import type {
  Artist,
  Conflict,
  Day,
  DayContext,
  Festival,
  FestivalSet,
  Id,
  LedgerRow,
  Member,
  MustSee,
  Note,
  Pick,
  Profile,
  Rating,
  Stage,
  SubgenreWeight,
  UserConstraint,
  Vote,
  WalkMatrix,
  Workspace,
} from '../engine/types.ts';
import {
  ARTISTS,
  DAYS,
  FESTIVAL,
  HOME_STAGE,
  LINEUP_VERSION,
  SETS,
  STAGES,
  WALK,
} from './lineup.ts';
import { accountStorage } from './db.ts';
import type { LocalStore } from './db.ts';
import { useSession } from './session.tsx';
import { restoreReviewedPlan } from './cloud-plan.ts';
import { readProfileForSolver } from './profile-defaults.ts';
import type { StoredProfile } from './profile-defaults.ts';


export interface AppState {
  ready: boolean;
  userId: Id;
  festival: Festival | null;
  days: Day[];
  stages: Stage[];
  walk: WalkMatrix | null;
  artists: Artist[];
  sets: FestivalSet[];
  profiles: StoredProfile[];
  ratings: Rating[];
  notes: Note[];
  mustSee: MustSee[];
  picks: Pick[];
  constraints: UserConstraint[];
  workspace: Workspace | null;
  members: Member[];
  ledger: LedgerRow[];
  votes: Vote[];
  config: TuningConfig;
  cachedAt?: string;
  onboarded: boolean;
}

export interface OnboardingAnswers {
  displayName: string;
  subgenreWeights: Record<string, 'love' | 'like' | 'no'>;
  // An absent answer stays absent in the stored profile.
  goalPriority?: Profile['goalPriority'];
  goalPriorities?: Profile['goalPriorities'];
  staminaCurve?: Profile['staminaCurve'];
  alignmentIntent?: Profile['alignmentIntent'];
  workspaceName: string;
}

export interface AppActions {
  completeOnboarding(answers: OnboardingAnswers): Promise<void>;
  rate(artistId: Id, score: number, source?: Rating['source']): Promise<void>;
  writeNote(artistId: Id, body: string): Promise<void>;
  setSeenBefore(artistId: Id, seenBefore: SeenBefore): Promise<void>;
  toggleTag(artistId: Id, name: string, intensity?: number): Promise<void>;
  setTopTen(order: Id[]): Promise<void>;
  toggleHardLock(artistId: Id): Promise<void>;
  setRestWindow(dayId: Id, startTs: number, minutes: number, active: boolean): Promise<void>;
  lockSet(setId: Id): Promise<void>;
  /** Adds or removes one set from the person's own plan. Changes nothing else. */
  togglePick(setId: Id): Promise<void>;
  /** Writes one solve per day as picks; refuses while picks exist unless told to replace them. */
  buildFirstDraft(options?: { replace?: boolean }): Promise<DraftOutcome>;
  /** Records an arrive-late or leave-early time the person chose for one pick. */
  setPickSpan(setId: Id, startTs?: number, endTs?: number): Promise<void>;
  /** Writes the spans and drops of one chosen option, and nothing beyond them. */
  applyClashOption(spans: { setId: Id; startTs: number; endTs: number }[], drops: Id[]): Promise<void>;
  clearPicks(dayId?: Id): Promise<void>;
  /** Replaces one day's picks outright, which is what accepting a draft does. */
  setDayPicks(dayId: Id, setIds: Id[]): Promise<void>;
  /** Everything this account would want back on another device. */
  cloudPayload(): Record<string, unknown>;
  /** Restores a validated account snapshot with a local backup and a revision guard. */
  restoreCloud(payload: Record<string, unknown>): Promise<number>;
  castVote(conflictId: Id, optionId: Id, tokenSpent: boolean): Promise<void>;
  applyResolution(conflictId: Id, optionId: Id): Promise<void>;
  commitIngest(payload: {
    festival: Festival;
    days: Day[];
    stages: Stage[];
    artists: Artist[];
    sets: FestivalSet[];
  }): Promise<void>;
  setWalk(next: WalkMatrix): Promise<void>;
  setConfig(next: TuningConfig): void;
  addCrewMember(displayName: string, taste?: Record<string, SubgenreWeight>): Promise<void>;
  cacheForOffline(): Promise<void>;
  /** Re-reads every store into state, after a background write. */
  reload(): Promise<void>;
  resetEverything(): Promise<void>;
}

export interface DraftOutcome {
  /** False when picks already existed and `replace` was not set. Nothing was written. */
  built: boolean;
  setCount: number;
  dayCount: number;
  infeasible: { dayId: Id; reason: string }[];
}

export interface DerivedState {
  /** The person's own plan, by day, in time order. Nothing derives this but their clicks. */
  picksByDay: Map<Id, ResolvedPick[]>;
  /** Overlaps and short walks between their picks, as notes rather than errors. */
  clashesByDay: Map<Id, Clash[]>;
  pickedSetIds: Set<Id>;
  dayContexts: DayContext[];
  utilityByDay: Map<Id, Map<Id, number>>;
  restByDay: Map<Id, RestRequirement[]>;
  solvesByDay: Map<Id, SolveResult>;
  conflictsByDay: Map<Id, Conflict[]>;
  artistById: Map<Id, Artist>;
  setById: Map<Id, FestivalSet>;
  stageById: Map<Id, Stage>;
  ratingByArtist: Map<Id, Rating>;
  myMustSee: MustSee[];
  hardLockSetIds: Id[];
  tokensRemaining: number;
}

const Ctx = createContext<{
  state: AppState;
  actions: AppActions;
  derived: DerivedState;
  bootError: string | null;
  storage: LocalStore;
} | null>(null);

const EMPTY: AppState = {
  ready: false,
  userId: 'me',
  festival: null,
  days: [],
  stages: [],
  walk: null,
  artists: [],
  sets: [],
  profiles: [],
  ratings: [],
  notes: [],
  mustSee: [],
  picks: [],
  constraints: [],
  workspace: null,
  members: [],
  ledger: [],
  votes: [],
  config: DEFAULT_CONFIG,
  onboarded: false,
};

const MEMBER_COLORS = ['#e0653a', '#4bb3c4', '#c8a33a', '#8f6fd1', '#5fa860', '#d1567f'];

export function StoreProvider({ children }: { children: ReactNode }) {
  const { storageUserId, known } = useSession();
  if (!known) return <p className="muted">Checking this browser for an account.</p>;
  const userId = storageUserId;
  return <AccountStore key={userId ?? 'guest'} userId={userId}>{children}</AccountStore>;
}

function AccountStore({ children, userId }: { children: ReactNode; userId: string | null }) {
  const [store] = useState(() => accountStorage(userId));
  const [state, setState] = useState<AppState>(EMPTY);

  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    let settled = false;

    // The bill is compiled into the application, so the schedule never needs
    // storage to be drawn. Storage carries what the person added to it.
    const fromBundle = (): AppState => ({
      ...EMPTY,
      ready: true,
      festival: FESTIVAL,
      days: [...DAYS].sort((a, b) => a.calendarDate.localeCompare(b.calendarDate)),
      stages: STAGES,
      walk: WALK,
      artists: ARTISTS,
      sets: SETS,
    });

    const giveUp = setTimeout(() => {
      if (settled) return;
      settled = true;
      setState(fromBundle());
      setBootError('this device is not saving anything at the moment');
    }, 1200);

    void (async () => {
      try {
        const stored = await store.meta<string>('lineupVersion');
        const existing = await store.getAll<Festival>('festivals');
        if (existing.length === 0 || stored !== LINEUP_VERSION) await installLineup(store);
        const next = await readAll(store);
        settled = true;
        clearTimeout(giveUp);
        setBootError(null);
        setState(next);
      } catch (error) {
        settled = true;
        clearTimeout(giveUp);
        setState(fromBundle());
        setBootError(error instanceof Error ? error.message : 'this device is not saving anything');
      }
    })();

    return () => clearTimeout(giveUp);
  }, []);

  const refresh = useCallback(async () => {
    setState(await readAll(store));
  }, []);

  const actions = useMemo<AppActions>(() => makeActions(state, refresh, setState, store), [state, refresh]);
  const derived = useDerived(state);

  return <Ctx.Provider value={{ state, actions, derived, bootError, storage: store }}>{children}</Ctx.Provider>;
}

export function useApp() {
  const value = useContext(Ctx);
  if (!value) throw new Error('useApp used outside the store provider');
  return value;
}

// Ratings, notes, locks and the crew survive this. Only the bill is replaced,
// which is what a published change to the set times amounts to.
async function installLineup(store: LocalStore): Promise<void> {
  // Picks that sat on a set whose time or stage moved, or which is gone, are
  // recorded before the bill is replaced, so the plan can say so rather than
  // quietly showing the new times.
  const previous = await store.getAll<FestivalSet>('sets');
  const picked = new Set((await store.getAll<Pick>('picks')).map((p) => p.setId));
  if (previous.length > 0 && picked.size > 0) {
    const changes = pickedLineupChanges(previous, SETS, ARTISTS, picked);
    if (changes.length > 0) {
      const held = (await store.meta<LineupChange[]>(LINEUP_CHANGES_KEY)) ?? [];
      await store.setMeta(LINEUP_CHANGES_KEY, [...held, ...changes]);
    }
  }
  await store.replaceAll('festivals', [FESTIVAL]);
  await store.replaceAll('days', DAYS);
  await store.replaceAll('stages', STAGES);
  await store.replaceAll('walk', [WALK]);
  await store.replaceAll('artists', ARTISTS);
  await store.replaceAll('sets', SETS);
  await store.replaceAll('plans', []);
  await store.setMeta('lineupVersion', LINEUP_VERSION);
}

async function readAll(store: LocalStore): Promise<AppState> {
  const [
    festivals,
    days,
    stages,
    walks,
    artists,
    sets,
    profiles,
    ratings,
    notes,
    mustSee,
    picks,
    constraints,
    workspaces,
    members,
    ledger,
    votes,
  ] = await Promise.all([
    store.getAll<Festival>('festivals'),
    store.getAll<Day>('days'),
    store.getAll<Stage>('stages'),
    store.getAll<WalkMatrix>('walk'),
    store.getAll<Artist>('artists'),
    store.getAll<FestivalSet>('sets'),
    store.getAll<StoredProfile>('profiles'),
    store.getAll<Rating>('ratings'),
    store.getAll<Note>('notes'),
    store.getAll<MustSee>('mustSee'),
    store.getAll<Pick>('picks'),
    store.getAll<UserConstraint>('constraints'),
    store.getAll<Workspace>('workspaces'),
    store.getAll<Member>('members'),
    store.getAll<LedgerRow>('ledger'),
    store.getAll<Vote>('votes'),
  ]);
  const cachedAt = await store.cachedAt();
  const overrides = await store.meta<Partial<TuningConfig>>('configOverrides');

  return {
    ready: true,
    userId: 'me',
    festival: festivals[0] ?? null,
    days: days.sort((a, b) => a.calendarDate.localeCompare(b.calendarDate)),
    stages,
    walk: walks[0] ?? null,
    artists,
    sets,
    profiles,
    ratings,
    notes,
    mustSee,
    picks,
    constraints,
    workspace: workspaces[0] ?? null,
    members,
    ledger,
    votes,
    config: overrides ? { ...DEFAULT_CONFIG, ...overrides } : DEFAULT_CONFIG,
    cachedAt,
    onboarded: profiles.some((p) => p.userId === 'me'),
  };
}

function makeActions(
  state: AppState,
  refresh: () => Promise<void>,
  setState: (fn: (prev: AppState) => AppState) => void,
  store: LocalStore,
): AppActions {
  const self = state.userId;

  return {
    async completeOnboarding(answers) {
      const profile: StoredProfile = {
        userId: self,
        subgenreWeights: answers.subgenreWeights,
        energyPref: 0.5,
        crowdTolerance: 0.5,
        alignedWith: [],
      };
      if (answers.staminaCurve) profile.staminaCurve = answers.staminaCurve;
      if (answers.goalPriority) profile.goalPriority = answers.goalPriority;
      if (answers.goalPriorities?.length) profile.goalPriorities = answers.goalPriorities;
      if (answers.alignmentIntent) profile.alignmentIntent = answers.alignmentIntent;
      await store.putOne('profiles', profile);

      const seeded = seedRatings(self, state.artists, answers.subgenreWeights, state.config);
      const manual = new Set(
        state.ratings.filter((r) => r.userId === self && r.source === 'manual').map((r) => r.artistId),
      );
      await store.putAll('ratings', seeded.filter((r) => !manual.has(r.artistId)));

      const workspace: Workspace = state.workspace ?? {
        id: `ws-${Math.random().toString(36).slice(2, 8)}`,
        festivalId: state.festival?.id ?? 'demo-festival',
        name: answers.workspaceName || 'My crew',
        joinCode: joinCode(),
        createdBy: self,
        settings: {
          hardLockCap: state.config.hardLockCap,
          tokenAllocation: state.config.tokenAllocation,
          tokensForceOutcome: false,
          scoreVisibility: 'banded',
          groupObjective: 'ledger',
          voteWindowMinutes: 30,
        },
      };
      await store.putOne('workspaces', workspace);
      await store.putOne('members', {
        workspaceId: workspace.id,
        userId: self,
        displayName: answers.displayName || 'You',
        color: MEMBER_COLORS[0],
        role: 'owner',
        joinedAt: new Date().toISOString(),
      });
      await store.putOne('ledger', {
        workspaceId: workspace.id,
        userId: self,
        cumulativeRegret: 0,
        decayedRegret: 0,
        tokensRemaining: workspace.settings.tokenAllocation,
      });
      await refresh();
    },

    async rate(artistId, score, source = 'manual') {
      const rating: Rating = {
        userId: self,
        artistId,
        score,
        source,
        confidence: source === 'manual' ? 1 : state.config.seedConfidence,
        updatedAt: new Date().toISOString(),
      };
      await store.putOne('ratings', rating);
      await store.queueWrite('rating', rating);
      await refresh();
    },

    async writeNote(artistId, body) {
      const existing = state.notes.find((n) => n.userId === self && n.artistId === artistId);
      const changed = (existing?.body ?? '') !== body;
      const note: Note = {
        ...existing,
        userId: self,
        artistId,
        body,
        // An edited body invalidates whatever was drawn from the old one, and
        // the revision is what a confirmation is checked against.
        revision: (existing?.revision ?? 0) + (changed ? 1 : 0),
        tags: changed ? (existing?.tags ?? []).filter((t) => t.confirmed) : (existing?.tags ?? []),
      };
      await store.putOne('notes', note);
      await store.queueWrite('note', note);
      await refresh();
    },

    async setSeenBefore(artistId, seenBefore) {
      const existing = state.notes.find((n) => n.userId === self && n.artistId === artistId);
      const note: Note = {
        ...existing,
        userId: self,
        artistId,
        body: existing?.body ?? '',
        seenBefore,
      };
      await store.putOne('notes', note);
      await store.queueWrite('note', note);
      await refresh();
    },

    // A tag is a confirmed statement by its author or it is nothing. Toggling
    // one off removes it rather than leaving it unconfirmed and still stored.
    async toggleTag(artistId, name, intensity = 2) {
      if (!EMOTIONS.has(name)) return;
      const existing = state.notes.find((n) => n.userId === self && n.artistId === artistId);
      const current = existing?.tags ?? [];
      const held = current.find((t) => t.name === name);
      const tags = held
        ? current.filter((t) => t.name !== name)
        : [...current, { name, intensity, confirmed: true }].slice(0, 12);

      const note: Note = {
        ...existing,
        userId: self,
        artistId,
        body: existing?.body ?? '',
        tags,
      };
      await store.putOne('notes', note);
      await store.queueWrite('note', note);
      await refresh();
    },

    async setTopTen(order) {
      const existing = state.mustSee.filter((m) => m.userId === self);
      for (const m of existing) await store.deleteOne('mustSee', [m.userId, m.artistId]);
      const kept = new Map(existing.map((m) => [m.artistId, m]));
      const rows: MustSee[] = order.slice(0, 10).map((artistId, i) => ({
        userId: self,
        artistId,
        rank: i + 1,
        hardLock: kept.get(artistId)?.hardLock ?? false,
      }));
      await store.putAll('mustSee', rows);
      await refresh();
    },

    async toggleHardLock(artistId) {
      const cap = state.workspace?.settings.hardLockCap ?? state.config.hardLockCap;
      const mine = state.mustSee.filter((m) => m.userId === self);
      const row = mine.find((m) => m.artistId === artistId);
      if (!row) return;
      const lockedCount = mine.filter((m) => m.hardLock).length;
      if (!row.hardLock && lockedCount >= cap) return;
      await store.putOne('mustSee', { ...row, hardLock: !row.hardLock });
      await refresh();
    },

    async setRestWindow(dayId, startTs, minutes, active) {
      const constraint: UserConstraint = {
        id: `rest:${self}:${dayId}`,
        userId: self,
        kind: 'rest_window',
        dayId,
        startTs,
        endTs: startTs + minutes * 60 * 1000,
        stageId: HOME_STAGE,
        label: 'Rest at camp',
        params: { minutes },
        active,
      };
      await store.putOne('constraints', constraint);
      await refresh();
    },

    async lockSet(setId) {
      const set = state.sets.find((s) => s.id === setId);
      if (!set) return;
      const mine = state.mustSee.filter((m) => m.userId === self);
      const row = mine.find((m) => m.artistId === set.artistId);
      if (row) {
        await store.putOne('mustSee', { ...row, hardLock: !row.hardLock });
      } else {
        const cap = state.workspace?.settings.hardLockCap ?? state.config.hardLockCap;
        if (mine.filter((m) => m.hardLock).length >= cap) return;
        await store.putOne('mustSee', {
          userId: self,
          artistId: set.artistId,
          rank: Math.min(10, mine.length + 1),
          hardLock: true,
        });
      }
      await refresh();
    },

    // Adding a set adds a set. It does not re-solve the day, move anything
    // else, or refuse because two picks overlap. Overlapping on purpose is a
    // thing people do, and it is not this code's business.
    async togglePick(setId) {
      const set = state.sets.find((s) => s.id === setId);
      if (!set) return;
      const held = state.picks.find((p) => p.userId === self && p.setId === setId);
      if (held) await store.deleteOne('picks', [self, setId]);
      else {
        await store.putOne('picks', {
          userId: self,
          setId,
          dayId: set.dayId,
          addedAt: new Date().toISOString(),
        });
      }
      await refresh();
    },

    // One solve per day and one write for the whole weekend. Adding a set later
    // still goes through togglePick, which never re-solves anything.
    async buildFirstDraft({ replace = false } = {}) {
      // Read from storage rather than this closure, so preferences saved a moment
      // ago in the same click are the ones the draft uses.
      const current = await readAll(store);
      if (current.picks.some((p) => p.userId === self) && !replace) {
        return { built: false, setCount: 0, dayCount: 0, infeasible: [] };
      }

      const walk = current.walk ?? { festivalId: '', minutes: {}, defaultMinutes: 12 };
      const draft = buildDraft({
        dayContexts: current.days.map((day) => ({
          day,
          stages: current.stages,
          sets: current.sets.filter((s) => s.dayId === day.id),
          walk,
        })),
        profile: readProfileForSolver(current.profiles.find((p) => p.userId === self), self),
        ratings: new Map(current.ratings.filter((r) => r.userId === self).map((r) => [r.artistId, r])),
        mustSee: current.mustSee.filter((m) => m.userId === self),
        artists: new Map(current.artists.map((a) => [a.id, a])),
        notes: new Map(current.notes.filter((n) => n.userId === self).map((n) => [n.artistId, n])),
        restByDay: restWindows(current),
        cfg: current.config,
        homeStageId: HOME_STAGE,
      });

      const now = new Date().toISOString();
      const table = draftPicks(current.picks, self, draft, replace, now);
      if (!table) return { built: false, setCount: 0, dayCount: 0, infeasible: [] };
      await store.replaceAll('picks', table);
      await store.setMeta('draftBuiltAt', now);
      await refresh();
      return {
        built: true,
        setCount: draft.picks.length,
        dayCount: draft.perDay.filter((d) => d.setIds.length > 0).length,
        infeasible: draft.perDay
          .filter((d) => d.infeasible)
          .map((d) => ({ dayId: d.dayId, reason: d.infeasible!.reason })),
      };
    },

    async setPickSpan(setId, startTs, endTs) {
      const held = state.picks.find((p) => p.userId === self && p.setId === setId);
      if (!held) return;
      await store.putOne('picks', { ...held, startTs, endTs });
      await refresh();
    },

    async applyClashOption(spans, drops) {
      for (const id of drops) await store.deleteOne('picks', [self, id]);
      for (const span of spans) {
        const held = state.picks.find((p) => p.userId === self && p.setId === span.setId);
        const set = state.sets.find((s) => s.id === span.setId);
        if (!set) continue;
        await store.putOne('picks', {
          userId: self,
          setId: span.setId,
          dayId: set.dayId,
          // A span equal to the whole set is stored as no override, so the pick
          // follows the set if its published time is corrected later.
          startTs: span.startTs > set.startTs ? span.startTs : undefined,
          endTs: span.endTs < set.endTs ? span.endTs : undefined,
          addedAt: held?.addedAt ?? new Date().toISOString(),
        });
      }
      await refresh();
    },

    async clearPicks(dayId) {
      const mine = state.picks.filter((p) => p.userId === self && (!dayId || p.dayId === dayId));
      for (const p of mine) await store.deleteOne('picks', [self, p.setId]);
      await refresh();
    },

    // One write pass rather than a clear followed by a run of toggles. A toggle
    // reads the picks this closure captured, so a loop of them after a clear
    // removes what it just added.
    async setDayPicks(dayId, setIds) {
      const mine = state.picks.filter((p) => p.userId === self && p.dayId === dayId);
      for (const p of mine) await store.deleteOne('picks', [self, p.setId]);
      const now = new Date().toISOString();
      for (const setId of setIds) {
        const set = state.sets.find((s) => s.id === setId);
        if (!set || set.dayId !== dayId) continue;
        await store.putOne('picks', { userId: self, setId, dayId, addedAt: now });
      }
      await refresh();
    },

    // The set times are not in here. They are compiled into the application and
    // are the same for everybody, so carrying a copy of them per account would
    // store the same megabyte once per person and let a stale copy override a
    // corrected one.
    cloudPayload() {
      return {
        picks: state.picks.filter((p) => p.userId === self),
        ratings: state.ratings.filter((r) => r.userId === self),
        notes: state.notes.filter((n) => n.userId === self),
        mustSee: state.mustSee.filter((m) => m.userId === self),
        profiles: state.profiles.filter((p) => p.userId === self),
        constraints: state.constraints,
        members: state.members,
        ledger: state.ledger,
        votes: state.votes,
        workspace: state.workspace,
        config: state.config,
      };
    },

    async restoreCloud(payload) {
      const rows = await restoreReviewedPlan(store, payload, this.cloudPayload());
      await refresh();
      return rows;
    },

    async castVote(conflictId, optionId, tokenSpent) {
      const vote: Vote = {
        conflictId,
        userId: self,
        optionId,
        tokenSpent,
        castAt: new Date().toISOString(),
        pending: !navigator.onLine,
      };
      await store.putOne('votes', vote);
      await store.queueWrite('vote', vote);
      if (tokenSpent) {
        const row = state.ledger.find((l) => l.userId === self);
        if (row && row.tokensRemaining > 0) {
          await store.putOne('ledger', { ...row, tokensRemaining: row.tokensRemaining - 1 });
        }
      }
      await refresh();
    },

    async applyResolution(conflictId, optionId) {
      await store.queueWrite('resolution', { conflictId, optionId });
      await store.setMeta(`resolution:${conflictId}`, optionId);
      await refresh();
    },

    async commitIngest(payload) {
      // The button is one gate and this is the other. A parse that produced no
      // sets replaces the whole bill with nothing, and the failure looks like a
      // successful import until somebody opens the schedule.
      if (payload.sets.length === 0) {
        throw new Error('An import with no sets in it does not replace the bill.');
      }
      await store.replaceAll('festivals', [payload.festival]);
      await store.replaceAll('days', payload.days);
      await store.replaceAll('stages', payload.stages);
      await store.replaceAll('artists', payload.artists);
      await store.replaceAll('sets', payload.sets);
      await store.replaceAll('plans', []);
      // A new bill invalidates whatever was verified against the old one.
      await store.setMeta('cachedAt', '');
      await refresh();
    },

    async setWalk(next) {
      await store.putOne('walk', next);
      await refresh();
    },

    setConfig(next) {
      void store.setMeta('configOverrides', next);
      setState((prev) => ({ ...prev, config: next }));
    },

    async addCrewMember(displayName, taste) {
      if (!state.workspace) return;
      const userId = `member-${Math.random().toString(36).slice(2, 8)}`;
      const index = state.members.length % MEMBER_COLORS.length;
      const weights = taste ?? {};
      await store.putOne('members', {
        workspaceId: state.workspace.id,
        userId,
        displayName,
        color: MEMBER_COLORS[index],
        role: 'member',
        joinedAt: new Date().toISOString(),
      });
      await store.putOne('ledger', {
        workspaceId: state.workspace.id,
        userId,
        cumulativeRegret: 0,
        decayedRegret: 0,
        tokensRemaining: state.workspace.settings.tokenAllocation,
      });
      await store.putOne('profiles', {
        userId,
        subgenreWeights: weights,
        energyPref: 0.5,
        staminaCurve: 'all_three_nights',
        crowdTolerance: 0.5,
        goalPriority: 'max_music',
        alignmentIntent: 'everyone',
        alignedWith: [],
      });
      // A member who joins for real does their own onboarding, and until they do,
      // every artist they have not rated is missing data rather than a zero. A
      // taste passed in here stands in for that onboarding.
      if (taste) {
        await store.putAll('ratings', seedRatings(userId, state.artists, weights, state.config));
      }
      await refresh();
    },

    // Reports cached only once the worker has confirmed every file in the
    // release is present. Writing the timestamp first said cached before the
    // download had started, which is the state somebody checks before losing
    // signal for three days.
    async reload() {
      await refresh();
    },

    async cacheForOffline() {
      if (!('serviceWorker' in navigator)) {
        throw new Error('This browser has no offline worker, so nothing can be verified.');
      }
      const reg = await navigator.serviceWorker.ready;
      const worker = reg.active;
      if (!worker) throw new Error('The offline worker is not running yet. Try again shortly.');

      const verified = await new Promise<{ complete: boolean; version?: string }>((resolve) => {
        const channel = new MessageChannel();
        const timer = setTimeout(() => resolve({ complete: false }), 20000);
        channel.port1.onmessage = (event) => {
          clearTimeout(timer);
          resolve({
            complete: Boolean(event.data?.complete),
            version: event.data?.version,
          });
        };
        worker.postMessage({ type: 'verify-offline' }, [channel.port2]);
      });

      if (!verified.complete) {
        throw new Error('The offline pack is not complete yet. Stay on a connection and try again.');
      }
      await store.setMeta('cachedAt', new Date().toISOString());
      if (verified.version) await store.setMeta('cachedVersion', verified.version);
      await refresh();
    },

    async resetEverything() {
      await store.wipe();
      await installLineup(store);
      await refresh();
    },
  };
}

function joinCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
}

function restWindows(state: AppState): Map<Id, RestRequirement[]> {
  const restByDay = new Map<Id, RestRequirement[]>();
  for (const day of state.days) {
    const explicit = state.constraints.filter(
      (c) =>
        c.userId === state.userId &&
        c.kind === 'rest_window' &&
        c.dayId === day.id &&
        c.active,
    );
    // No default window. There is no rest area at this festival: people sit
    // in the grass at whichever stage they are already at, and eat when they
    // are hungry. A block of time reserved at camp is not a thing anybody
    // planned, and reserving one made whole days unsolvable.
    restByDay.set(
      day.id,
      explicit.map((c) => ({
        id: c.id,
        stageId: c.stageId ?? HOME_STAGE,
        startTs: c.startTs!,
        endTs: c.endTs!,
        label: c.label,
        utility: 0.25,
      })),
    );
  }
  return restByDay;
}

function useDerived(state: AppState): DerivedState {
  return useMemo(() => {
    const artistById = new Map(state.artists.map((a) => [a.id, a]));
    const setById = new Map(state.sets.map((s) => [s.id, s]));
    const stageById = new Map(state.stages.map((s) => [s.id, s]));
    const ratingByArtist = new Map(
      state.ratings.filter((r) => r.userId === state.userId).map((r) => [r.artistId, r]),
    );
    const myMustSee = state.mustSee
      .filter((m) => m.userId === state.userId)
      .sort((a, b) => a.rank - b.rank);
    const myNotes = new Map(
      state.notes.filter((n) => n.userId === state.userId).map((n) => [n.artistId, n]),
    );

    const walk = state.walk ?? { festivalId: '', minutes: {}, defaultMinutes: 12 };
    const dayContexts: DayContext[] = state.days.map((day) => ({
      day,
      stages: state.stages,
      sets: state.sets.filter((s) => s.dayId === day.id),
      walk,
    }));

    const profileFor = (userId: Id): Profile =>
      readProfileForSolver(state.profiles.find((p) => p.userId === userId), userId);

    const restByDay = restWindows(state);

    const hardLockArtistIds = new Set(myMustSee.filter((m) => m.hardLock).map((m) => m.artistId));
    const hardLockSetIds = state.sets.filter((s) => hardLockArtistIds.has(s.artistId)).map((s) => s.id);

    const myPicks = state.picks.filter((p) => p.userId === state.userId);
    const pickedSetIds = new Set(myPicks.map((p) => p.setId));
    const picksByDay = new Map<Id, ResolvedPick[]>();
    const clashesByDay = new Map<Id, Clash[]>();
    for (const ctx of dayContexts) {
      const resolved = resolvePicks(
        myPicks.filter((p) => p.dayId === ctx.day.id),
        setById,
      );
      picksByDay.set(ctx.day.id, resolved);
      clashesByDay.set(ctx.day.id, findClashes(resolved, walk));
    }

    const utilityByDay = new Map<Id, Map<Id, number>>();
    const solvesByDay = new Map<Id, SolveResult>();
    dayContexts.forEach((ctx, dayIndex) => {
      const utility = buildUtilityMap(ctx.sets, {
        profile: profileFor(state.userId),
        ratings: ratingByArtist,
        mustSee: myMustSee,
        artists: artistById,
        cfg: state.config,
        dayIndex,
        notes: myNotes,
      });
      utilityByDay.set(ctx.day.id, utility);
      solvesByDay.set(
        ctx.day.id,
        solveDay({
          ctx,
          utility,
          hardLockSetIds,
          rest: restByDay.get(ctx.day.id) ?? [],
          homeStageId: HOME_STAGE,
          cfg: state.config,
        }),
      );
    });

    const conflictsByDay = new Map<Id, Conflict[]>();
    const workspace = state.workspace;
    if (workspace && state.members.length > 1) {
      const views = buildMemberViews(
        new Map(
          state.members.map((m) => [
            m.userId,
            {
              displayName: m.displayName,
              ratings: state.ratings.filter((r) => r.userId === m.userId),
            },
          ]),
        ),
        state.config,
      );
      const weights = ledgerWeights(state.ledger, state.config);

      dayContexts.forEach((ctx, dayIndex) => {
        const plans = state.members.map((m) => {
          const memberRatings = new Map(
            state.ratings.filter((r) => r.userId === m.userId).map((r) => [r.artistId, r]),
          );
          const memberMustSee = state.mustSee.filter((x) => x.userId === m.userId);
          const utility = buildUtilityMap(ctx.sets, {
            profile: profileFor(m.userId),
            ratings: memberRatings,
            mustSee: memberMustSee,
            artists: artistById,
            cfg: state.config,
            dayIndex,
          });
          const lockedArtists = new Set(
            memberMustSee.filter((x) => x.hardLock).map((x) => x.artistId),
          );
          const locks = ctx.sets.filter((s) => lockedArtists.has(s.artistId)).map((s) => s.id);
          const result = solveDay({
            ctx,
            utility,
            hardLockSetIds: locks,
            rest: m.userId === state.userId ? restByDay.get(ctx.day.id) ?? [] : [],
            homeStageId: HOME_STAGE,
            cfg: state.config,
          });
          return {
            userId: m.userId,
            displayName: m.displayName,
            plan: planFromSolve('user', m.userId, ctx.day.id, result),
            hardLockSetIds: locks,
          };
        });

        conflictsByDay.set(
          ctx.day.id,
          detectConflicts({
            workspaceId: workspace.id,
            dayId: ctx.day.id,
            plans,
            sets: ctx.sets,
            stages: state.stages,
            walk,
            group: { views, weights, objective: workspace.settings.groupObjective },
            settings: workspace.settings,
            notes: state.notes,
            cfg: state.config,
          }),
        );
      });
    }

    const tokensRemaining =
      state.ledger.find((l) => l.userId === state.userId)?.tokensRemaining ??
      state.workspace?.settings.tokenAllocation ??
      state.config.tokenAllocation;

    return {
      picksByDay,
      clashesByDay,
      pickedSetIds,
      dayContexts,
      utilityByDay,
      restByDay,
      solvesByDay,
      conflictsByDay,
      artistById,
      setById,
      stageById,
      ratingByArtist,
      myMustSee,
      hardLockSetIds,
      tokensRemaining,
    };
  }, [state]);
}
